/** Recharge theme-app-extension mount (not `<recharge-legacy-widget>`). */
const RECHARGE_APP_BLOCK_SELECTOR =
  '[id*="__recharge_subscriptions_subscription_widget_"], .recharge-subscription-widget';

/** @type {Promise<object> | null} */
let productJsonPromise = null;

/**
 * Move the live Recharge app-block mount + selling_plan input into the cloned form
 * so `product-form` replaceWith does not tear down Recharge's initialized DOM.
 * @param {Element} currentProductForm
 * @param {Element} nextProductForm
 */
function transplantRechargeAppBlock(currentProductForm, nextProductForm) {
  const liveAppBlock = currentProductForm.querySelector(RECHARGE_APP_BLOCK_SELECTOR);
  const appBlockSlotInClone = nextProductForm.querySelector(RECHARGE_APP_BLOCK_SELECTOR);
  if (liveAppBlock && appBlockSlotInClone) appBlockSlotInClone.replaceWith(liveAppBlock);

  const liveSellingPlanInput = currentProductForm.querySelector('form input[name="selling_plan"]');
  const nextForm = nextProductForm.querySelector('form');
  if (!liveSellingPlanInput || !nextForm) return;

  nextForm.querySelector('input[name="selling_plan"]')?.remove();
  nextForm.prepend(liveSellingPlanInput);
}

/**
 * @param {{ productId: string | number, variantId: string | number, productHandle?: string }} params
 */
async function syncRechargeWidget({ productId, variantId, productHandle }) {
  const numericProductId = Number(productId);
  const numericVariantId = Number(variantId);
  if (!productId || !variantId || !numericProductId || !numericVariantId) return;

  syncVariantIdInputs(numericProductId, String(variantId));
  dispatchRechargeVariantChange(numericProductId, numericVariantId);
  await toggleRechargeWidgetVisibility(numericVariantId, productHandle);
}

/**
 * @param {number} productId
 * @param {number} variantId
 */
function dispatchRechargeVariantChange(productId, variantId) {
  window.Recharge?.eventService?.dispatchEvent?.(`${productId}:variant:change`, variantId);

  const state = window.Recharge?.widgets?.[productId]?.state;
  if (state) state.variant = variantId;
}

/**
 * @param {number} variantId
 * @param {string} [productHandle]
 */
async function toggleRechargeWidgetVisibility(variantId, productHandle) {
  try {
    const product = await getProductJson(productHandle);
    const variant = product?.variants?.find((item) => item.id === variantId);
    const display = variant?.selling_plan_allocations?.length ? '' : 'none';

    document.querySelectorAll(RECHARGE_APP_BLOCK_SELECTOR).forEach((appBlock) => {
      appBlock.style.display = display;
    });
  } catch {
    // Widget visibility is best-effort; don't block variant updates.
  }
}

/**
 * @param {string} [productHandle]
 * @returns {Promise<object>}
 */
function getProductJson(productHandle) {
  if (productJsonPromise) return productJsonPromise;

  const root = window.Shopify?.routes?.root || '/';
  const productJsonUrl = productHandle
    ? `${root}products/${productHandle}.js`
    : `${window.location.pathname.replace(/\/$/, '')}.js`;

  productJsonPromise = fetch(productJsonUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch product JSON (${response.status})`);
      return response.json();
    })
    .catch((error) => {
      productJsonPromise = null;
      throw error;
    });

  return productJsonPromise;
}

/**
 * @param {HTMLInputElement} input
 * @param {string} value
 */
function setInputValue(input, value) {
  input.value = value;
  input.setAttribute('value', value);
}

/**
 * @param {number} productId
 * @param {string} variantId
 */
function syncVariantIdInputs(productId, variantId) {
  const form = document.querySelector(`product-form[data-product-id="${productId}"] form`);
  if (!form) return;

  let idInput = form.querySelector('input[name="id"]');
  if (!idInput) {
    idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    form.prepend(idInput);
  }

  setInputValue(idInput, variantId);
  idInput.dispatchEvent(new Event('change', { bubbles: true }));

  const variantIdInput = form.querySelector('input[name="variant-id"]');
  if (variantIdInput) setInputValue(variantIdInput, variantId);
}

/**
 * @param {ParentNode} parent
 * @param {string} selector
 * @param {string} tagName
 * @param {string} className
 * @returns {Element}
 */
function ensureElement(parent, selector, tagName, className) {
  let el = parent.querySelector(selector);
  if (el) return el;

  el = document.createElement(tagName);
  el.className = className;
  return el;
}

/**
 * @description Legacy `.rc-widget` label/USP customization + Recharge variant sync.
 */
class RechargeLegacyWidget extends HTMLElement {
  /** @type {RechargeLegacyConfig | null} */
  #legacyConfig = null;
  /** @type {HTMLElement | null} */
  #host = null;
  /** @type {MutationObserver | null} */
  #mountObserver = null;

  connectedCallback() {
    this.#host = this.closest('product-form');
    this.#legacyConfig = this.#getLegacyConfig();
    if (this.#host && this.#legacyConfig) this.#startObservers();
    this.#syncInitialVariant();
  }

  disconnectedCallback() {
    this.#mountObserver?.disconnect();
    this.#mountObserver = null;
  }

  /**
   * Called by `<product-form>` before `replaceWith` so the live Recharge app block survives.
   * @param {Element} nextProductForm
   */
  preserveAppBlockAcrossSwap(nextProductForm) {
    if (!this.#host || !nextProductForm) return;
    transplantRechargeAppBlock(this.#host, nextProductForm);
  }

  /**
   * Called by `<product-form>` after `replaceWith` to notify Recharge of the new variant.
   * @param {string} [variantId]
   */
  syncVariant(variantId) {
    if (!variantId) return;

    syncRechargeWidget({
      productId: this.dataset.productId,
      variantId,
      productHandle: this.dataset.productHandle,
    });
  }

  #syncInitialVariant() {
    const variantId =
      this.#host?.querySelector('input[name="variant-id"]')?.value ||
      document.querySelector(
        `product-variant-select[data-product-id="${this.dataset.productId}"] [data-variant-id]:checked`
      )?.dataset.variantId;

    if (variantId) this.syncVariant(variantId);
  }

  #startObservers() {
    const widget = this.#findRcWidget();
    if (widget) {
      this.#applyLegacyWidgetConfig(widget);
      return;
    }
    this.#watchMount();
  }

  /** @returns {Element | null} */
  #findRcWidget() {
    return this.#host?.querySelector('.rc-widget') ?? null;
  }

  #watchMount() {
    if (!this.#host) return;

    this.#mountObserver?.disconnect();
    this.#mountObserver = new MutationObserver(() => {
      const widget = this.#findRcWidget();
      if (!widget) return;

      this.#mountObserver?.disconnect();
      this.#mountObserver = null;
      this.#applyLegacyWidgetConfig(widget);
    });
    this.#mountObserver.observe(this.#host, { childList: true, subtree: true });
  }

  /** @returns {RechargeLegacyConfig | null} */
  #getLegacyConfig() {
    const el = this.querySelector('#recharge-legacy-subscription-widget');
    if (!el) return null;

    const raw = (el instanceof HTMLTemplateElement ? el.innerHTML : el.textContent)?.trim();
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {Element} widget
   */
  #applyLegacyWidgetConfig(widget) {
    if (!this.#legacyConfig) return;

    this.#applyLegacySubscriptionLabel(widget);
    this.#applyLegacySubscriptionUsps(widget);
    this.#syncLegacyState(widget);

    if (widget.dataset.legacyWidgetEventsBound === 'true') return;

    const sync = () => this.#syncLegacyState(widget);
    widget.addEventListener('change', sync);
    widget.addEventListener('click', sync);
    widget.dataset.legacyWidgetEventsBound = 'true';
  }

  /**
   * @param {Element} widget
   */
  #applyLegacySubscriptionLabel(widget) {
    const label = String(this.#legacyConfig?.label ?? '').trim();
    if (!label) return;

    const discountLabel = widget.querySelector('.subscription-radio .discount-label');
    if (discountLabel) discountLabel.textContent = label;
  }

  /**
   * @param {Element} widget
   */
  #applyLegacySubscriptionUsps(widget) {
    const usps = [1, 2, 3]
      .map((idx) => String(this.#legacyConfig?.[`usp_${idx}`] ?? '').trim())
      .filter(Boolean);
    if (!usps.length) return;

    const subscriptionRadio = widget.querySelector('.subscription-radio');
    if (!subscriptionRadio) return;

    const uspWrapper = ensureElement(
      widget,
      '.sellingplan-usp-wrapper',
      'div',
      'sellingplan-usp-wrapper'
    );
    const uspList = ensureElement(widget, '.sellingplan-usps', 'ul', 'sellingplan-usps');

    if (!subscriptionRadio.contains(uspWrapper)) subscriptionRadio.appendChild(uspWrapper);

    const sellingPlans = widget.querySelector('.rc-selling-plans');
    if (sellingPlans) subscriptionRadio.insertBefore(sellingPlans, uspWrapper);

    if (!uspWrapper.contains(uspList)) uspWrapper.appendChild(uspList);

    subscriptionRadio.classList.add('flex-wrap');
    uspList.classList.add('basis-full');
    uspList.replaceChildren(
      ...usps.map((usp) => {
        const li = document.createElement('li');
        li.textContent = usp;
        return li;
      })
    );
  }

  /**
   * @param {Element} widget
   */
  #syncLegacyState(widget) {
    const uspList = widget.querySelector('.sellingplan-usps');
    const uspWrapper = widget.querySelector('.sellingplan-usp-wrapper');
    if (!uspList || !uspWrapper) return;

    const hideUsps = !this.#isSubscriptionSelected(widget);
    uspList.classList.toggle('hidden', hideUsps);
    uspWrapper.classList.toggle('hidden', hideUsps);
  }

  /**
   * @param {Element} widget
   * @returns {boolean}
   */
  #isSubscriptionSelected(widget) {
    const subscriptionRadio = widget.querySelector('.subscription-radio');
    if (subscriptionRadio?.querySelector('input[type="radio"]')?.checked) return true;
    if (subscriptionRadio?.classList.contains('rc-radio--active')) return true;

    const oneTimeInput = widget.querySelector('.onetime-radio input[type="radio"]');
    return Boolean(oneTimeInput && !oneTimeInput.checked);
  }
}

customElements.define('recharge-legacy-widget', RechargeLegacyWidget);
